import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import html2canvas from 'html2canvas';
import { environment } from 'src/environments/environment';

export interface ScreenshotOptions {
  type?: 'image/png' | 'image/jpeg';
  quality?: number;
  scale?: number;
  backgroundColor?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ScreenshotService {
  private inlinedSrcByUrl = new Map<string, string>();
  private readonly inlineAttr = 'data-screenshot-inline';
  private readonly canvasAttr = 'data-screenshot-canvas';

  constructor(private http: HttpClient) {}

  /**
   * Captures the full content of an element, including overflow/scrolled areas.
   * @param element Target HTML element (div) to screenshot
   * @returns Base64 data URL of the screenshot
   */
  async captureElement(element: HTMLElement, options: ScreenshotOptions = {}): Promise<string> {
    const canvas = await this.renderToCanvas(element, options);
    const { type = 'image/jpeg', quality = 0.85 } = options;
    return canvas.toDataURL(type, quality);
  }

  /**
   * Same capture as captureElement, but returns binary image data as a Blob.
   */
  async captureElementAsBlob(element: HTMLElement, options: ScreenshotOptions = {}): Promise<Blob> {
    const canvas = await this.renderToCanvas(element, options);
    return this.canvasToBlob(canvas, options);
  }

  private async renderToCanvas(element: HTMLElement, options: ScreenshotOptions): Promise<HTMLCanvasElement> {
    if (!(element instanceof HTMLElement)) {
      throw new Error('Screenshot target element is required');
    }

    const {
      scale = 2,
      backgroundColor = '#ffffff'
    } = options;

    const restoreCanvasSnapshots = this.snapshotCanvases(element);
    const restoreFooter = this.hideFooterForCapture(element);
    const restoreStyles = this.expandScrollAncestors(element);
    const restoreScroll = this.resetScroll(element);
    const restoreFocus = this.clearFocus();
    this.stampSignaturePreviewImages(element);
    const restoreImages = await this.inlineImages(element);

    try {
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
      await this.waitForImages(element);
      await this.nextPaint();
      void element.offsetHeight;
      await this.restoreCanvasPixelsFromSnapshots(element);

      const captureWidth = Math.max(
        element.scrollWidth,
        element.offsetWidth,
        element.clientWidth,
        1
      );
      const width = captureWidth;
      const height = Math.max(element.scrollHeight, element.offsetHeight, element.clientHeight, 1);
      const safeScale = this.clampScale(width, height, scale);

      // The clone renders inside an iframe sized to these values. Sizing it to
      // the element instead of the viewport moves the clone to another
      // breakpoint (and rescales vh units), so the rendered layout no longer
      // matches what was measured here.
      const viewportWidth = Math.max(window.innerWidth || 0, document.documentElement.clientWidth || 0, width, 1);
      const viewportHeight = Math.max(window.innerHeight || 0, document.documentElement.clientHeight || 0, 1);

      // No width/height/x/y: html2canvas then crops to the cloned element's own
      // box, measured after `onclone` expanded it, so the whole element is kept
      // instead of just the part that fit on screen.
      const canvas = await html2canvas(element, {
        scale: safeScale,
        useCORS: true,
        allowTaint: false,
        imageTimeout: 15000,
        backgroundColor,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        windowWidth: viewportWidth,
        windowHeight: viewportHeight,
        ignoreElements: (node) => this.shouldExcludeFromCapture(node),
        onclone: async (clonedDoc, clonedElement) => {
          const root = clonedElement || clonedDoc.body || clonedDoc.documentElement;
          this.prepareClone(clonedDoc, root, captureWidth, element);
          if (clonedDoc.body && clonedDoc.body !== root) {
            this.applyInlinedImages(clonedDoc.body);
            this.normalizeImages(clonedDoc.body);
          }
          // html2canvas skips images whose naturalWidth is still 0, so wait for
          // the swapped-in signature/data-URL images to decode first.
          await this.waitForImages(root);
        }
      });

      if (!canvas.width || !canvas.height) {
        throw new Error('Screenshot canvas has zero dimensions');
      }

      return canvas;
    } finally {
      restoreImages();
      restoreScroll();
      restoreStyles();
      restoreFooter();
      restoreFocus();
      restoreCanvasSnapshots();
    }
  }

  private canvasToBlob(canvas: HTMLCanvasElement, options: ScreenshotOptions): Promise<Blob> {
    const { type = 'image/jpeg', quality = 0.85 } = options;

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create screenshot blob'));
          return;
        }
        resolve(blob);
      }, type, quality);
    });
  }

  private prepareClone(
    clonedDoc: Document,
    clonedElement: HTMLElement,
    captureWidth: number,
    liveRoot?: HTMLElement,
  ): void {
    this.unfixHeader(clonedElement);
    this.prepareCaptureRoot(clonedElement, captureWidth);
    // this.prepareFinancePanelCapture(clonedElement, captureWidth);
    this.hideFooter(clonedDoc, clonedElement);
    this.expandOverflow(clonedElement);
    this.preserveBusinessNameEllipsis(clonedElement, captureWidth);
    this.replaceButtonsWithDivs(clonedDoc, clonedElement);
    this.replaceCanvasesWithImages(clonedDoc, clonedElement, liveRoot);
    // this.ensureSignaturePreviewVisible(clonedElement);
    this.applyInlinedImages(clonedElement);
    this.normalizeImages(clonedElement);
  }

  private snapshotCanvases(element: HTMLElement): () => void {
    const marked: HTMLCanvasElement[] = [];
    element.querySelectorAll('canvas').forEach((canvas) => {
      if (!canvas.width || !canvas.height) {
        return;
      }
      try {
        canvas.setAttribute(this.canvasAttr, canvas.toDataURL('image/png'));
        marked.push(canvas);
      } catch {
        // Tainted canvas: let html2canvas try its own clone path.
      }
    });
    return () => marked.forEach((canvas) => canvas.removeAttribute(this.canvasAttr));
  }

  private replaceCanvasesWithImages(
    clonedDoc: Document,
    clonedRoot: HTMLElement,
    liveRoot?: HTMLElement,
  ): void {
    const clonedView = clonedDoc.defaultView;
    const liveScope = liveRoot ? this.getSignatureScope(liveRoot) : liveRoot;
    const clonedScope = this.getSignatureScope(clonedRoot);

    if (liveRoot && clonedView && liveScope) {
      const liveCanvases = this.getVisibleCanvases(liveScope, window);
      const clonedCanvases = this.getVisibleCanvases(clonedScope, clonedView);
      const count = Math.min(liveCanvases.length, clonedCanvases.length);
      for (let i = 0; i < count; i++) {
        const dataUrl = liveCanvases[i].getAttribute(this.canvasAttr);
        if (!dataUrl) {
          continue;
        }
        this.replaceCanvasWithImage(clonedDoc, clonedCanvases[i], dataUrl);
      }
    }

    clonedScope.querySelectorAll<HTMLCanvasElement>(`canvas[${this.canvasAttr}]`).forEach((canvas) => {
      const dataUrl = canvas.getAttribute(this.canvasAttr);
      const computed = clonedView?.getComputedStyle(canvas);
      if (!dataUrl || computed?.display === 'none') {
        return;
      }
      this.replaceCanvasWithImage(clonedDoc, canvas, dataUrl);
    });
  }

  private getVisibleCanvases(root: HTMLElement, view: Window): HTMLCanvasElement[] {
    return Array.from(root.querySelectorAll('canvas')).filter((canvas) => {
      if (this.isHiddenElement(canvas, view)) {
        return false;
      }
      return canvas.width > 0 && canvas.height > 0;
    });
  }

  private replaceCanvasWithImage(
    clonedDoc: Document,
    canvas: HTMLCanvasElement,
    dataUrl: string,
  ): void {
    const computed = clonedDoc.defaultView?.getComputedStyle(canvas);
    const img = clonedDoc.createElement('img');
    img.src = dataUrl;
    img.style.display = computed?.display === 'inline' ? 'inline-block' : (computed?.display || 'block');
    img.style.width = computed?.width || `${canvas.width}px`;
    img.style.height = computed?.height || `${canvas.height}px`;
    // Keep the image inside its wrapper so it can't paint over the right border.
    img.style.boxSizing = computed?.boxSizing || 'border-box';
    img.style.maxWidth = '100%';
    img.style.border = computed?.border || '';
    img.style.borderRadius = computed?.borderRadius || '';
    img.style.backgroundColor = '#ffffff';
    canvas.replaceWith(img);
  }

  private restoreCanvasPixelsFromSnapshots(element: HTMLElement): Promise<void> {
    const tasks: Promise<void>[] = [];
    element.querySelectorAll<HTMLCanvasElement>(`canvas[${this.canvasAttr}]`).forEach((canvas) => {
      const dataUrl = canvas.getAttribute(this.canvasAttr);
      if (!dataUrl || this.isHiddenCanvas(canvas)) {
        return;
      }
      tasks.push(new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0);
          resolve();
        };
        img.onerror = () => resolve();
        img.src = dataUrl;
      }));
    });
    return Promise.all(tasks).then(() => undefined);
  }

  private getSignatureScope(root: HTMLElement): HTMLElement {
    return root.querySelector<HTMLElement>('#canvasDiv') || root;
  }

  private stampSignaturePreviewImages(element: HTMLElement): void {
    const scope = this.getSignatureScope(element);
    scope.querySelectorAll('img').forEach((img) => {
      if (this.isHiddenElement(img)) {
        return;
      }
      const src = img.currentSrc || img.getAttribute('src') || img.src;
      if (!src) {
        return;
      }
      if (src.startsWith('data:') || src.startsWith('blob:')) {
        img.setAttribute(this.inlineAttr, src);
      }
    });
  }

  private ensureSignaturePreviewVisible(root: HTMLElement): void {
    const scope = this.getSignatureScope(root);
    const view = scope.ownerDocument.defaultView || window;
    const layoutNodes = new Set<HTMLElement>([scope]);
    scope.querySelectorAll<HTMLElement>(
      '.signature--pad__wrapper, .tabs--body, .generate_img, app-generate-signature'
    ).forEach((node) => layoutNodes.add(node));
    layoutNodes.forEach((node) => {
      node.style.setProperty('overflow', 'visible', 'important');
      node.style.setProperty('max-height', 'none', 'important');
      node.style.setProperty('height', 'auto', 'important');
    });
    scope.querySelectorAll('img').forEach((img) => {
      if (this.isHiddenElement(img, view)) {
        return;
      }
      img.style.visibility = 'visible';
      img.style.opacity = '1';
      img.style.display = 'block';
      img.style.maxWidth = '100%';
    });
  }

  private isHiddenCanvas(canvas: HTMLCanvasElement): boolean {
    return this.isHiddenElement(canvas);
  }

  private isHiddenElement(element: HTMLElement, view: Window = window): boolean {
    const computed = view.getComputedStyle(element);
    return computed.display === 'none' || computed.visibility === 'hidden';
  }

  private replaceButtonsWithDivs(clonedDoc: Document, root: HTMLElement): void {
    root.querySelectorAll('button').forEach((button) => {
      const computed = clonedDoc.defaultView?.getComputedStyle(button);
      const div = clonedDoc.createElement('div');

      Array.from(button.attributes).forEach((attr) => {
        if (attr.name === 'type') {
          return;
        }
        div.setAttribute(attr.name, attr.value);
      });

      while (button.firstChild) {
        div.appendChild(button.firstChild);
      }

      if (computed) {
        div.style.display = computed.display === 'inline' ? 'inline-block' : computed.display;
        div.style.boxSizing = computed.boxSizing;
        div.style.width = computed.width;
        div.style.maxWidth = computed.maxWidth;
        div.style.height = computed.height;
        div.style.padding = computed.padding;
        div.style.margin = computed.margin;
        div.style.border = computed.border;
        div.style.borderRadius = computed.borderRadius;
        div.style.backgroundColor = computed.backgroundColor;
        div.style.color = computed.color;
        div.style.font = computed.font;
        div.style.boxShadow = computed.boxShadow;
        div.style.textAlign = computed.textAlign;
      }

      button.replaceWith(div);
    });
  }

  private normalizeImages(root: HTMLElement): void {
    root.querySelectorAll('img').forEach((img) => {
      const src = img.getAttribute('src') || img.src || '';
      const resolved = this.resolveImageUrl(src);
      if (resolved && resolved !== src) {
        img.setAttribute('src', resolved);
      }
      img.removeAttribute('srcset');
      img.style.visibility = 'visible';
      img.style.opacity = '1';

      const isInlined = resolved.startsWith('data:') || resolved.startsWith('blob:');
      if (isInlined) {
        img.removeAttribute('crossorigin');
      }

      if (
        img.classList.contains('new-img')
        || img.classList.contains('business-logo')
        || img.classList.contains('-business-logo')
        || img.closest('.business-logo-outer')
      ) {
        img.style.visibility = 'visible';
        img.style.opacity = '1';
        img.style.display = 'block';
      }
    });
  }

  private expandScrollAncestors(element: HTMLElement): () => void {
    const changedElements: Array<{ element: HTMLElement; style: string | null }> = [];

    const expand = (current: HTMLElement): void => {
      const style = current.ownerDocument.defaultView?.getComputedStyle(current);
      const isScrollable = ['auto', 'scroll', 'hidden'].includes(style?.overflowY || '')
        || ['auto', 'scroll', 'hidden'].includes(style?.overflowX || '')
        || ['auto', 'scroll', 'hidden'].includes(style?.overflow || '');

      if (isScrollable && !this.shouldPreserveOverflow(current, style)) {
        changedElements.push({
          element: current,
          style: current.getAttribute('style')
        });
        current.style.setProperty('overflow', 'visible', 'important');
        current.style.setProperty('overflow-x', 'visible', 'important');
        current.style.setProperty('overflow-y', 'visible', 'important');
        current.style.setProperty('max-height', 'none', 'important');
        current.style.setProperty('height', 'auto', 'important');
      }
    };

    let ancestor: HTMLElement | null = element;
    while (ancestor) {
      expand(ancestor);
      ancestor = ancestor.parentElement;
    }

    element.querySelectorAll<HTMLElement>('*').forEach(expand);
    void element.offsetHeight;

    return () => {
      changedElements.forEach((item) => {
        if (item.style === null) {
          item.element.removeAttribute('style');
        } else {
          item.element.setAttribute('style', item.style);
        }
      });
    };
  }

  private resetScroll(element: HTMLElement): () => void {
    const nodes: Array<{ element: Element; top: number; left: number }> = [];

    const collect = (current: Element | null): void => {
      if (!current) {
        return;
      }
      nodes.push({
        element: current,
        top: current.scrollTop,
        left: current.scrollLeft
      });
      current.scrollTop = 0;
      current.scrollLeft = 0;
    };

    collect(element);
    element.querySelectorAll('*').forEach(collect);

    let parent = element.parentElement;
    while (parent) {
      collect(parent);
      parent = parent.parentElement;
    }

    const windowX = window.scrollX;
    const windowY = window.scrollY;
    if (windowX || windowY) {
      window.scrollTo(0, 0);
    }

    return () => {
      nodes.forEach((item) => {
        item.element.scrollTop = item.top;
        item.element.scrollLeft = item.left;
      });
      if (windowX || windowY) {
        window.scrollTo(windowX, windowY);
      }
    };
  }

  private clearFocus(): () => void {
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }

    return () => {
      if (active instanceof HTMLElement) {
        active.focus({ preventScroll: true });
      }
    };
  }

  /**
   * html2canvas clones the page and reloads image URLs. Remote header logos
   * disappear in that clone because of CORS. Resolve them to data URLs and
   * stamp them on a data attribute so the clone can use them even if Angular
   * [src] bindings or URL matching would otherwise miss the image.
   */
  private async inlineImages(element: HTMLElement): Promise<() => void> {
    this.inlinedSrcByUrl.clear();
    const images = Array.from(element.querySelectorAll('img'));
    const marked: Array<{ image: HTMLImageElement; src: string | null }> = [];

    await Promise.all(images.map(async (image) => {
      const originalAttr = image.getAttribute('src');
      const originalSrc = originalAttr || image.currentSrc || image.src;
      if (!originalSrc) {
        return;
      }

      if (originalSrc.startsWith('data:') || originalSrc.startsWith('blob:')) {
        image.setAttribute(this.inlineAttr, originalSrc);
        marked.push({ image, src: originalAttr });
        await this.waitForImage(image);
        return;
      }

      const resolvedSrc = this.resolveImageUrl(originalSrc);
      try {
        await this.waitForImage(image);
        const imageUrl = this.resolveImageUrl(image.currentSrc || image.src || resolvedSrc);
        const dataUrl = this.loadedImageToDataUrl(image)
          || await this.loadCorsImageDataUrl(imageUrl)
          || await this.loadImageDataUrl(imageUrl);
        if (!dataUrl) {
          return;
        }

        marked.push({ image, src: originalAttr });
        image.setAttribute(this.inlineAttr, dataUrl);
        image.removeAttribute('crossorigin');
        image.removeAttribute('srcset');
        image.setAttribute('src', dataUrl);
        image.src = dataUrl;
        this.rememberInlinedSrc(originalSrc, dataUrl);
        this.rememberInlinedSrc(resolvedSrc, dataUrl);
        this.rememberInlinedSrc(imageUrl, dataUrl);
        await this.waitForImage(image);
      } catch {
        // Keep the original URL; html2canvas will still try useCORS itself.
      }
    }));

    return () => {
      marked.forEach(({ image, src }) => {
        image.removeAttribute(this.inlineAttr);
        if (src === null) {
          image.removeAttribute('src');
        } else {
          image.setAttribute('src', src);
        }
      });
      this.inlinedSrcByUrl.clear();
    };
  }

  private rememberInlinedSrc(src: string, dataUrl: string): void {
    if (!src || src.startsWith('data:') || src.startsWith('blob:')) {
      return;
    }
    this.inlinedSrcByUrl.set(src, dataUrl);
    this.inlinedSrcByUrl.set(src.replace(/\\/g, '/'), dataUrl);
    this.inlinedSrcByUrl.set(this.resolveImageUrl(src), dataUrl);
  }

  private applyInlinedImages(root: HTMLElement): void {
    root.querySelectorAll('img').forEach((img) => {
      const stamped = img.getAttribute(this.inlineAttr);
      const candidates = [
        stamped || '',
        img.getAttribute('src') || '',
        img.src,
        img.currentSrc
      ];
      const dataUrl = stamped || candidates
        .map((src) => this.inlinedSrcByUrl.get(src)
          || this.inlinedSrcByUrl.get(src.replace(/\\/g, '/'))
          || this.inlinedSrcByUrl.get(this.resolveImageUrl(src)))
        .find(Boolean);
      if (!dataUrl) {
        return;
      }

      img.removeAttribute('srcset');
      img.removeAttribute('crossorigin');
      img.setAttribute('src', dataUrl);
      img.src = dataUrl;
    });
  }

  private unfixHeader(root: HTMLElement): void {
    root.querySelectorAll<HTMLElement>('.header__container, .customer_background').forEach((node) => {
      const style = node.ownerDocument.defaultView?.getComputedStyle(node);
      if (!style || style.position !== 'fixed') {
        return;
      }
      node.style.setProperty('position', 'relative', 'important');
      node.style.setProperty('top', 'auto', 'important');
      node.style.setProperty('left', 'auto', 'important');
      node.style.setProperty('right', 'auto', 'important');
      node.style.setProperty('z-index', '1', 'important');
    });
  }

  /**
   * Hide page footers for the capture only. Restore them afterward so the
   * live page footer stays unchanged.
   */
  private hideFooterForCapture(element: HTMLElement): () => void {
    const restorers: Array<() => void> = [];
    const snapshot = (node: HTMLElement) => {
      const style = node.getAttribute('style');
      const className = node.getAttribute('class');
      restorers.push(() => {
        if (className === null) {
          node.removeAttribute('class');
        } else {
          node.setAttribute('class', className);
        }
        if (style === null) {
          node.removeAttribute('style');
        } else {
          node.setAttribute('style', style);
        }
      });
    };

    snapshot(element);
    element.querySelectorAll<HTMLElement>(
      '.panel-outer--mainwrapper, .screen_manager, .progress-main--wrapper, .second-step--outerwrap, .header__container, .customer_background, .finance-main--wrapper__left, .finance-main--wrapper__right, .finance-main--wrapper__button, .button-last, .confirm-pay, .scroll-container, .step-container, .top_side_bar, .top_main_container, .tabs--body, .generate_img'
    ).forEach((node) => snapshot(node));
    this.prepareCaptureRoot(element);
    // this.prepareFinancePanelCapture(element);
    this.unfixHeader(element);

    return () => restorers.reverse().forEach((restore) => restore());
  }

  private hideFooter(clonedDoc: Document, root: HTMLElement): void {
    this.collectFooters(root, clonedDoc).forEach((footer) => {
      footer.remove();
    });
  }

  private shouldExcludeFromCapture(node: Element): boolean {
    if (!(node instanceof HTMLElement)) {
      return false;
    }
    return node.matches('footer, .footer, .plans_data_information_amount')
      || node.hasAttribute('data-screenshot-exclude')
      || !!node.closest('footer, .footer, .plans_data_information_amount, [data-screenshot-exclude]');
  }

  private prepareCaptureRoot(root: HTMLElement, captureWidth?: number): void {
    // const isFinancePanel = this.isFinancePanelRoot(root);

    // if (!isFinancePanel) {
    //   root.style.setProperty('display', 'block', 'important');
    //   root.style.setProperty('border-radius', '0', 'important');
    // }
    root.style.setProperty('align-items', 'stretch', 'important');
    root.style.setProperty('justify-content', 'flex-start', 'important');
    if (root?.className !== 'section-main finance-panel-web') {
      root.style.setProperty('height', 'auto', 'important');
    }
    root.style.setProperty('max-height', 'none', 'important');
    root.style.setProperty('overflow', 'visible', 'important');
    root.style.setProperty('margin', '0', 'important');
    root.style.setProperty('box-sizing', 'border-box', 'important');
    // if (!isFinancePanel) {
    //   root.style.setProperty('width', captureWidth ? `${captureWidth}px` : '100%', 'important');
    //   root.style.setProperty('max-width', captureWidth ? `${captureWidth}px` : '100%', 'important');
    // }

    root.querySelectorAll<HTMLElement>(
      '.panel-outer--mainwrapper, .screen_manager, .progress-main--wrapper, .second-step--outerwrap'
    ).forEach((node) => {
      node.style.setProperty('border-radius', '0', 'important');
      node.style.setProperty('width', '100%', 'important');
      node.style.setProperty('max-width', '100%', 'important');
      if (root?.className !== 'section-main finance-panel-web') {
      node.style.setProperty('height', 'auto', 'important');
    }
      node.style.setProperty('max-height', 'none', 'important');
      node.style.setProperty('overflow', 'visible', 'important');
      node.style.setProperty('margin-left', '0', 'important');
      node.style.setProperty('margin-right', '0', 'important');
    });
  }

  // private isFinancePanelRoot(root: HTMLElement): boolean {
  //   return root.classList.contains('finance-main--wrapper')
  //     || root.classList.contains('finance_panel');
  // }

  /**
   * The finance confirm screen is an absolutely positioned, transformed card
   * with inner scroll. html2canvas clips that layout (heading cut, button
   * overflow) unless we flatten it and keep the black border.
   */
  // private prepareFinancePanelCapture(root: HTMLElement, captureWidth?: number): void {
  //   const panel = this.isFinancePanelRoot(root)
  //     ? root
  //     : root.querySelector<HTMLElement>('.finance-main--wrapper');
  //   if (!panel) {
  //     return;
  //   }

  //   const width = captureWidth
  //     || Math.max(Math.round(panel.getBoundingClientRect().width), panel.offsetWidth, 1);

  //   panel.style.setProperty('position', 'relative', 'important');
  //   panel.style.setProperty('transform', 'none', 'important');
  //   panel.style.setProperty('top', 'auto', 'important');
  //   panel.style.setProperty('left', 'auto', 'important');
  //   panel.style.setProperty('right', 'auto', 'important');
  //   panel.style.setProperty('bottom', 'auto', 'important');
  //   panel.style.setProperty('display', 'flex', 'important');
  //   panel.style.setProperty('flex-wrap', 'wrap', 'important');
  //   panel.style.setProperty('align-items', 'stretch', 'important');
  //   panel.style.setProperty('height', 'auto', 'important');
  //   panel.style.setProperty('max-height', 'none', 'important');
  //   panel.style.setProperty('overflow', 'visible', 'important');
  //   panel.style.setProperty('overflow-x', 'visible', 'important');
  //   panel.style.setProperty('overflow-y', 'visible', 'important');
  //   panel.style.setProperty('width', `${width}px`, 'important');
  //   panel.style.setProperty('max-width', `${width}px`, 'important');
  //   panel.style.setProperty('margin', '0', 'important');
  //   panel.style.setProperty('border', '2px solid #0a0a0a', 'important');
  //   panel.style.setProperty('border-radius', '15px', 'important');
  //   panel.style.setProperty('box-sizing', 'border-box', 'important');

  //   panel.querySelectorAll<HTMLElement>(
  //     '.finance-main--wrapper__left, .finance-main--wrapper__right, .top_side_bar, .top_main_container, .confirm-pay, .scroll-container, .step-container, .signature--pad__wrapper, .tabs--body, .signature-wrapper, .generate_img, app-generate-signature'
  //   ).forEach((node) => {
  //     node.style.setProperty('height', 'auto', 'important');
  //     node.style.setProperty('max-height', 'none', 'important');
  //     node.style.setProperty('overflow', 'visible', 'important');
  //     node.style.setProperty('overflow-x', 'visible', 'important');
  //     node.style.setProperty('overflow-y', 'visible', 'important');
  //   });

  //   const view = panel.ownerDocument.defaultView || window;
  //   const left = panel.querySelector<HTMLElement>('.finance-main--wrapper__left');
  //   // On narrow layouts the left column becomes the top business bar, so it is
  //   // only collapsed when the stylesheet itself hides it.
  //   const leftHidden = !!left && this.isHiddenElement(left, view);
  //   if (left) {
  //     if (leftHidden) {
  //       left.style.setProperty('display', 'none', 'important');
  //       left.style.setProperty('width', '0', 'important');
  //       left.style.setProperty('min-height', '0', 'important');
  //       left.style.setProperty('padding', '0', 'important');
  //       left.style.setProperty('margin', '0', 'important');
  //     } else {
  //       left.style.setProperty('align-self', 'stretch', 'important');
  //     }
  //   }

  //   const right = panel.querySelector<HTMLElement>('.finance-main--wrapper__right');
  //   if (right && (leftHidden || width < 768)) {
  //     right.style.setProperty('width', '100%', 'important');
  //     right.style.setProperty('max-width', '100%', 'important');
  //     right.style.setProperty('flex', '1 1 100%', 'important');
  //   }

  //   panel.querySelectorAll<HTMLElement>('.finance-main--wrapper__button, .button-last').forEach((node) => {
  //     node.style.setProperty('position', 'relative', 'important');
  //     node.style.setProperty('bottom', 'auto', 'important');
  //     node.style.setProperty('top', 'auto', 'important');
  //     node.style.setProperty('left', 'auto', 'important');
  //     node.style.setProperty('right', 'auto', 'important');
  //     node.style.setProperty('margin-top', '24px', 'important');
  //     node.style.setProperty('padding-bottom', '8px', 'important');
  //     node.style.setProperty('z-index', '1', 'important');
  //   });
  // }

  private collectFooters(root: HTMLElement, doc: Document | null): HTMLElement[] {
    const found = new Set<HTMLElement>();
    const add = (node: Element | null) => {
      if (!(node instanceof HTMLElement) || !this.isCaptureFooter(node)) {
        return;
      }
      found.add(node);
    };

    root.querySelectorAll('footer, .footer, .plans_data_information_amount, [data-screenshot-exclude]').forEach((node) => add(node));

    const parent = root.parentElement;
    if (parent) {
      Array.from(parent.children).forEach((child) => {
        if (child !== root) {
          add(child);
        }
      });
    }

    if (doc) {
      doc.querySelectorAll('footer, .footer, .plans_data_information_amount, [data-screenshot-exclude]').forEach((node) => add(node));
    }

    return Array.from(found);
  }

  private isCaptureFooter(element: HTMLElement): boolean {
    return element.matches('footer, .footer, .plans_data_information_amount, [data-screenshot-exclude]');
  }

  private isHeaderLogoShell(element: HTMLElement): boolean {
    return element.classList.contains('business-logo-outer')
      || element.classList.contains('new-img')
      || element.classList.contains('-business-logo')
      || element.classList.contains('header__container')
      || element.classList.contains('customer_details_name')
      || element.classList.contains('customer_details_two')
      || element.classList.contains('customer_details_two_part')
      || element.classList.contains('business_name')
      || element.classList.contains('step_comp')
      || !!element.closest('.business_name');
  }

  private shouldPreserveOverflow(
    element: HTMLElement,
    style?: CSSStyleDeclaration | null
  ): boolean {
    if (this.isHeaderLogoShell(element)) {
      return true;
    }
    return style?.textOverflow === 'ellipsis';
  }

  /**
   * html2canvas often drops media-query ellipsis. Re-apply the clip in the
   * clone and truncate the name so it cannot paint over "Full Details".
   */
  private preserveBusinessNameEllipsis(root: HTMLElement, captureWidth: number): void {
    const isNarrow = (window.innerWidth || captureWidth) <= 767;
    if (!isNarrow) {
      return;
    }

    const fallbackWidth = Math.max(64, Math.round(captureWidth * 0.7 - 88));

    root.querySelectorAll<HTMLElement>('.business_name').forEach((wrap) => {
      wrap.style.setProperty('min-width', '0', 'important');
      wrap.style.setProperty('max-width', '100%', 'important');
      wrap.style.setProperty('overflow', 'hidden', 'important');

      wrap.querySelectorAll('p').forEach((node) => {
        const el = node as HTMLElement;
        el.style.setProperty('max-width', '100%', 'important');
        el.style.setProperty('overflow', 'hidden', 'important');
        el.style.setProperty('overflow-x', 'hidden', 'important');
        el.style.setProperty('text-overflow', 'ellipsis', 'important');
        el.style.setProperty('white-space', 'nowrap', 'important');
        this.truncateToWidth(el, el.clientWidth || wrap.clientWidth || fallbackWidth);
      });
    });
  }

  private truncateToWidth(element: HTMLElement, maxWidth: number): void {
    const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text || maxWidth <= 0) {
      return;
    }

    const style = element.ownerDocument.defaultView?.getComputedStyle(element);
    const canvas = element.ownerDocument.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.font = style?.font || '700 18px Inter, sans-serif';
    if (ctx.measureText(text).width <= maxWidth) {
      return;
    }

    const ellipsis = '…';
    let low = 0;
    let high = text.length;
    let best = ellipsis;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const candidate = `${text.slice(0, mid).trimEnd()}${ellipsis}`;
      if (ctx.measureText(candidate).width <= maxWidth) {
        best = candidate;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    element.textContent = best;
  }

  private resolveImageUrl(src: string): string {
    if (!src) {
      return '';
    }
    const normalized = src.replace(/\\/g, '/');
    if (normalized.startsWith('data:') || normalized.startsWith('blob:')) {
      return normalized;
    }
    if (normalized.startsWith('//')) {
      return `${window.location.protocol}${normalized}`;
    }
    try {
      return new URL(normalized, document.baseURI).href;
    } catch {
      return normalized;
    }
  }

  private async loadCorsImageDataUrl(url: string): Promise<string | null> {
    if (!url || url.startsWith('data:') || url.startsWith('blob:')) {
      return null;
    }

    return new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      let settled = false;
      const done = (dataUrl: string | null) => {
        if (settled) {
          return;
        }
        settled = true;
        image.onload = null;
        image.onerror = null;
        resolve(dataUrl);
      };
      image.onload = () => done(this.loadedImageToDataUrl(image));
      image.onerror = () => done(null);
      window.setTimeout(() => done(null), 5000);
      image.src = url;
    });
  }

  private async loadImageDataUrl(url: string): Promise<string | null> {
    const resolved = this.resolveImageUrl(url);
    if (!resolved) {
      return null;
    }

    const proxyUrl = `${environment.business_url}image_proxy`;

    try {
      const response = await this.http.post(
        proxyUrl,
        { url: resolved },
        { responseType: 'text' },
      ).toPromise();
      const payload = JSON.parse(response || '{}');
      const baseData = payload?.base_data || payload?.data || payload?.image;
      if (baseData) {
        return String(baseData).startsWith('data:')
          ? baseData
          : `data:${payload?.mime_type || 'image/png'};base64,${baseData}`;
      }
    } catch {
      // Keep the original URL; html2canvas will still try useCORS itself.
    }

    return null;
  }

  private waitForImage(image: HTMLImageElement): Promise<void> {
    if (image.complete && image.naturalWidth > 0) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      const done = () => {
        image.removeEventListener('load', done);
        image.removeEventListener('error', done);
        resolve();
      };

      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
      window.setTimeout(done, 5000);
    });
  }

  private loadedImageToDataUrl(img: HTMLImageElement): string | null {
    if (!img.naturalWidth || !img.naturalHeight) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }

    try {
      context.drawImage(img, 0, 0);
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  }

  private waitForImages(element: HTMLElement): Promise<void> {
    const images = Array.from(element.querySelectorAll('img'));
    return Promise.all(images.map((img) => this.waitForImage(img))).then(() => undefined);
  }

  private nextPaint(): Promise<void> {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }

  private clampScale(width: number, height: number, scale: number): number {
    const maxDimension = 16384;
    const maxArea = 16777216;
    const scaledWidth = width * scale;
    const scaledHeight = height * scale;
    const widthScale = scaledWidth > maxDimension ? maxDimension / width : scale;
    const heightScale = scaledHeight > maxDimension ? maxDimension / height : scale;
    const areaScale = (width * height * scale * scale) > maxArea
      ? Math.sqrt(maxArea / (width * height))
      : scale;
    return Math.max(0.5, Math.min(scale, widthScale, heightScale, areaScale));
  }

  private expandOverflow(element: HTMLElement): void {
    const style = element.ownerDocument.defaultView?.getComputedStyle(element);
    const overflowY = style?.overflowY || '';
    const overflow = style?.overflow || '';

    if (
      !this.shouldPreserveOverflow(element, style)
      && (['auto', 'scroll', 'hidden'].includes(overflowY) || ['auto', 'scroll', 'hidden'].includes(overflow))
    ) {
      element.style.overflow = 'visible';
      element.style.overflowY = 'visible';
      element.style.maxHeight = 'none';
      element.style.height = 'auto';
    }

    Array.from(element.children).forEach((child) => {
      if (child instanceof HTMLElement) {
        this.expandOverflow(child);
      }
    });
  }
}
